import React, { useState, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Form, Field } from "react-final-form";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Message } from "primereact/message";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import { ISignupFormData, ISubscriptionPlan } from "../../interface/pricing";
import { getPlanConfiguration } from "../../config/pricingPlans";
import { httpPost } from "../../services/httpService";
import { SSLPadlock as SSLSecureImage } from "../../public/assets/images/pricing";
import { ApiEndPoints } from "../../utils/endpoints";
import Footer from "../../components/layout/footer";

interface ZuoraDigitalSignature {
  tenantId: string;
  token: string;
  signature: string;
  key: string;
}

interface ZuoraResponse {
  pageId: string;
  DigitalSignature: ZuoraDigitalSignature;
}

const getPlanFromParams = (pmId: string | null, billingCycle: "monthly" | "annual" | null, coupon?: string | null) => {
  const planMap: Record<string, string> = {
    PRO1: "starter",
    PRORP1: "professional",
    PRORPA1: "advanced",
    PRORPA1Y: "advanced",
    PRO4: "starter",
    PRORP4: "professional",
    PRORPA5: "advanced",
  };

  const planId = planMap[pmId || ""] || "starter";

  const isAnnualPlan = ["PRO4", "PRORP4", "PRORPA5", "PRORPA1Y"].includes(pmId || "");
  const effectiveBillingCycle = isAnnualPlan ? "annual" : billingCycle || "monthly";

  const planConfiguration = getPlanConfiguration(planId, effectiveBillingCycle, coupon);

  return planConfiguration;
};

const ContactDetailsStep: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useRef<Toast>(null);

  const pmId = searchParams.get("pmId");

  const coupon = searchParams.get("Coupon") || searchParams.get("coupon");
  const billingCycle = searchParams.get("billingCycle")
    ? (searchParams.get("billingCycle") as "monthly" | "annual")
    : null;

  const affiliate = searchParams.get("Affiliate") || searchParams.get("affiliate");

  const selectedPlan: ISubscriptionPlan = location.state?.plan || getPlanFromParams(pmId, billingCycle, coupon);

  const required = (value: any) => (value ? undefined : "This field is required");

  const minLength = (min: number) => (value: string) => {
    if (!value) return undefined;
    return value.length >= min ? undefined : `Must be at least ${min} characters`;
  };

  const composeValidators =
    (...validators: any[]) =>
    (value: any) =>
      validators.reduce((error, validator) => error || validator(value), undefined);

  const isEmail = (value: string) => {
    if (!value) return undefined;
    return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value) ? undefined : "Invalid email address";
  };

  const isMobile = (value: string) => {
    if (!value) return undefined;
    return /^[0-9]{10}$/.test(value) ? undefined : "Please enter a valid 10-digit mobile number";
  };

  const maxLength = (max: number) => (value: string) => {
    if (!value) return undefined;
    return value.trim().length <= max ? undefined : `Must be ${max} characters or less`;
  };

  const isValidName = (value: string) => {
    if (!value) return undefined;
    return /^[A-Za-z\s'-]+$/.test(value.trim()) ? undefined : "Only letters are allowed";
  };

  const mustBeChecked = (value: boolean) => (value ? undefined : "You must accept the terms and conditions");

  const isArchistarInclusive = selectedPlan.features.find(
    (feature) => feature === "Archistar - discover profitable, underdeveloped sites"
  );

  const isRPDataInclusive = selectedPlan.features.find(
    (feature) => feature === "Nationwide CoreLogic RP Data Professional"
  );
  const hasRPDataAndArchistar = Boolean(isRPDataInclusive && isArchistarInclusive);

  const isAdvancedAnnualSignup = selectedPlan.planName === "Advanced" && selectedPlan.billingCycle === "annual";
  const isAdvancedAnnualUpfrontPlan = selectedPlan.pmId === "PRORPA1Y";

  const onSubmit = async (values: ISignupFormData) => {
    if (!pmId) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Missing a Product Code.",
        life: 5000,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const cleanedMobile = values.mobile.replace(/[\s\-\(\)]/g, "");

      const formData = {
        Firstname: values.firstName,
        Lastname: values.lastName,
        Mobile: values.mobile,
        Email: values.email,
        EmailConfirm: values.confirmEmail,
        expect_to_buy: "5",
        biggest_challenge: "8",
        terms: "on",
        Coupon: coupon || "",
        Country: "AU",
        ProductCode: pmId,
        site: "new",
        "": "",
      };

      await httpPost(`${ApiEndPoints.changeEmail}/ZSignature`, formData)
        .then((res) => {
          const zuoraData = res.data as unknown as ZuoraResponse;
          if (res.status === 200 && zuoraData?.DigitalSignature && zuoraData.pageId) {
            const zuoraConfig = {
              tenantId: zuoraData.DigitalSignature.tenantId,
              token: zuoraData.DigitalSignature.token,
              signature: zuoraData.DigitalSignature.signature,
              key: zuoraData.DigitalSignature.key,
              url: ApiEndPoints.paymentPageUrl,
              style: "inline",
              id: zuoraData.pageId,
              submitEnabled: "true",
            };

            navigate("/signup/payment", {
              state: {
                zuoraConfig,
                selectedPlan,
                affiliate,
                contactDetails: {
                  firstName: values.firstName,
                  lastName: values.lastName,
                  email: values.email,
                  mobile: cleanedMobile,
                },
              },
            });
          } else {
            throw new Error(res.error || "Failed to get Zuora signature");
          }
        })
        .catch((err) => {
          toast.current?.show({
            severity: "error",
            summary: "Payment Setup Failed",
            detail: err.message || "Unable to process your request. Please try again or contact support.",
            life: 5000,
          });
          return { error: err.message };
        })
        .finally(() => {});
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.message || "Failed to proceed. Please try again.",
        life: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!selectedPlan) {
    navigate("/pricing");
    return null;
  }

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <div className="signup-container min-h-screen bg-gradient-to-b from-gray-50 to-white py-6 sm:py-8 md:py-12 lg:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-8 sm:mb-10 md:mb-12 max-w-4xl mx-auto">
            <>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 leading-tight">
                {selectedPlan.planName} Membership Sign Up
              </h1>
            </>
          </div>

          <div className="grid lg:grid-cols-5 gap-6 sm:gap-8 lg:gap-10">
            <div className="lg:col-span-2 order-2 lg:order-1">
              <Card className="lg:sticky lg:top-6 shadow-lg border border-gray-200">
                <div className="p-5 sm:p-6">
                  <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-5 text-gray-900">{selectedPlan.planName}</h2>
                  <div className="text-3xl sm:text-4xl font-bold text-blue-600 mb-4 sm:mb-5">
                    ${selectedPlan.displayPrice}
                    <span className="text-lg sm:text-xl font-normal text-gray-600 ml-1">
                      /{selectedPlan.billingCycle === "annual" ? "year" : "month"}
                    </span>
                  </div>

                  {selectedPlan.planName === "Professional" && (
                    <div className="rounded-xl border border-sky-100 bg-sky-50/70 px-4 py-3 text-sm text-sky-900 mb-4 mt-5">
                      Includes <span className="font-semibold">Australia-wide Cotality RP Data access</span>
                    </div>
                  )}
                  {isAdvancedAnnualUpfrontPlan && (
                    <div className="rounded-xl border border-sky-100 bg-sky-50/70 px-4 py-3 text-sm text-sky-900 mb-4 mt-5">
                      Membership includes{" "}
                      <span className="font-semibold">
                        Cotality RP Data & Archistar Consumer Australia wide access!
                      </span>
                    </div>
                  )}
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mt-3 mb-1">
                    Join the Real Estate Investar {selectedPlan.planName} Membership
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 mb-4 leading-relaxed">
                    Unlock powerful tools to help you search, analyse and track great property investment opportunities.
                  </p>
                  {selectedPlan.billingCycle === "monthly" ? (
                    <>
                      <h3 className="text-base sm:text-lg font-semibold mb-3 text-gray-900">
                        Month‑to‑Month Membership
                      </h3>
                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-2 sm:p-3 rounded-xl border border-gray-200">
                        <div className="mb-4 rounded-lg border border-sky-100 bg-white/80">
                          <ul className="mt-3 space-y-2 pl-5 text-gray-700 leading-relaxed list-disc marker:text-sky-500">
                            {hasRPDataAndArchistar ? (
                              <li>
                                <b>
                                  Your membership includes Australia-wide access to Cotality RP Data and Archistar
                                  Consumer.
                                </b>
                              </li>
                            ) : (
                              isRPDataInclusive && (
                                <li>
                                  <b>Your membership includes Australia-wide access to Cotality RP Data.</b>
                                </li>
                              )
                            )}
                            {!hasRPDataAndArchistar && isArchistarInclusive && (
                              <li>
                                <b>Your membership includes access to Archistar consumer.</b>
                              </li>
                            )}
                            <li>
                              <b>
                                Membership is available only to individual users who utilise{" "}
                                {isRPDataInclusive ? "Cotality RP Data" : ""}
                                {isAdvancedAnnualUpfrontPlan && isRPDataInclusive && isArchistarInclusive
                                  ? " and Archistar"
                                  : " "}
                                {!isAdvancedAnnualUpfrontPlan && isArchistarInclusive ? "and Archistar consumer" : ""}{" "}
                                for personal investment purposes.
                              </b>
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div className="mt-4 space-y-3">
                        <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 sm:p-4">
                          {selectedPlan.planName === "Advanced" ? (
                            <>
                              <p className="text-sm sm:text-base text-gray-700 mb-2 leading-relaxed">
                                You are signing up for <b>casual Professional</b> and <b>Archistar membership</b>.
                              </p>
                              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                                You will be charged a total of <b>${selectedPlan.displayPrice} today</b>, with the same
                                amount billed on the <b>same calendar day each month</b> to maintain ongoing access.
                              </p>
                              <p className="text-sm sm:text-base text-gray-700 mt-2 leading-relaxed">
                                This monthly fee consists of{" "}
                                {selectedPlan.displayPrice === 248 && (
                                  <>
                                    <b>$149 for Professional membership including Cotality RP Data access</b> and{" "}
                                    <b>$99 for Archistar Consumer access</b>
                                  </>
                                )}
                                {selectedPlan.displayPrice === 228 && (
                                  <>
                                    <b>$129 for Professional and RP Data membership</b> and{" "}
                                    <b>$99 for Archistar Consumer access</b>
                                  </>
                                )}
                                {selectedPlan.displayPrice === 2480 && (
                                  <>
                                    <b>$1490 for Professional membership including Cotality RP Data access</b> and{" "}
                                    <b>$990 for Archistar Consumer access</b>
                                  </>
                                )}
                              </p>
                            </>
                          ) : (
                            <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                              You will be charged <b>${selectedPlan.displayPrice} today</b>, with the same amount billed
                              on the <b>same calendar day each month</b> to maintain ongoing membership access.
                            </p>
                          )}
                        </div>
                        <div className="rounded-lg border border-amber-100 bg-amber-50 p-3 sm:p-4">
                          <p className="text-sm sm:text-base text-amber-800 leading-relaxed">
                            You may cancel your membership at any time, as there is <b>no minimum term</b>. To request
                            cancellation, please provide at least <b>14 days' notice</b> prior to your renewal date by
                            emailing <b>info@realestateinvestar.com.au</b>.
                          </p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200">
                      <h3 className="text-base sm:text-lg font-semibold mb-3 text-gray-900">
                        1 year upfront membership
                      </h3>
                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-2 sm:p-3 rounded-xl border border-gray-200">
                        <div className="mb-4 rounded-lg border border-sky-100 bg-white/80">
                          <ul className="mt-3 space-y-2 pl-5 text-gray-700 leading-relaxed list-disc marker:text-sky-500">
                            {hasRPDataAndArchistar ? (
                              <li>
                                <b>
                                  Your membership includes Australia-wide access to Cotality RP Data and Archistar
                                  Consumer.
                                </b>
                              </li>
                            ) : (
                              isRPDataInclusive && (
                                <li>
                                  <b>Your membership includes Australia-wide access to Cotality RP Data.</b>
                                </li>
                              )
                            )}
                            {!hasRPDataAndArchistar && isArchistarInclusive && (
                              <li>
                                <b>Your membership includes access to Archistar consumer.</b>
                              </li>
                            )}
                            <li>
                              <b>
                                Membership is available only to individual users who utilise{" "}
                                {isRPDataInclusive ? "Cotality RP Data" : ""}
                                {isAdvancedAnnualUpfrontPlan && isRPDataInclusive && isArchistarInclusive
                                  ? " and Archistar"
                                  : " "}
                                {!isAdvancedAnnualUpfrontPlan && isArchistarInclusive ? "and Archistar consumer" : ""}{" "}
                                for personal investment purposes.
                              </b>
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div className="mt-4 space-y-3">
                        <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 sm:p-4 space-y-2">
                          <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                            You are signing up for our <b>1 year upfront membership.</b>
                          </p>
                          {isAdvancedAnnualSignup ? (
                            <>
                              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                                You will be charged <b>${selectedPlan.displayPrice} upfront membership</b>
                              </p>
                              {isAdvancedAnnualUpfrontPlan && (
                                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                                  This annual fee consists of{" "}
                                  {coupon ? (
                                    <>
                                      <b>$1290 for Professional membership including Cotality RP Data access</b> and{" "}
                                      <b>$990 for Archistar Consumer access.</b>
                                    </>
                                  ) : (
                                    <>
                                      <b>$1490 for Professional membership including Cotality RP Data access</b> and{" "}
                                      <b>$990 for Archistar Consumer access.</b>
                                    </>
                                  )}
                                </p>
                              )}
                            </>
                          ) : (
                            <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                              You will be charged <b>${selectedPlan.displayPrice}</b>, which provides{" "}
                              <b>12 months of continuous membership access.</b>
                            </p>
                          )}
                        </div>
                        <div className="rounded-lg border border-amber-100 bg-amber-50 p-3 sm:p-4">
                          <p className="text-sm sm:text-base text-amber-800 leading-relaxed">
                            Your membership will automatically renew annually, and we'll notify you in advance before
                            any renewal is processed.{" "}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-5 sm:mt-6 pt-5 border-t border-gray-200 flex items-center justify-center">
                    <img src={SSLSecureImage} alt="SSL Secure" className="h-12 sm:h-14 w-auto" />
                  </div>
                </div>
              </Card>
            </div>

            <div className="lg:col-span-3 order-1 lg:order-2">
              <Card className="shadow-lg border border-gray-200">
                <div className="p-5 sm:p-6 md:p-8">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 text-gray-900">
                    Step 1 of 2: Contact Details
                  </h2>
                  <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">
                    Please fill in your information to continue
                  </p>

                  <Form
                    onSubmit={onSubmit}
                    initialValues={{
                      firstName: "",
                      lastName: "",
                      email: "",
                      confirmEmail: "",
                      mobile: "",
                      acceptTerms: false,
                    }}
                    validate={(values) => {
                      const errors: any = {};
                      if (values.confirmEmail !== values.email) {
                        errors.confirmEmail = "Emails do not match";
                      }
                      return errors;
                    }}
                    render={({ handleSubmit, values }) => (
                      <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
                        <Field
                          name="firstName"
                          validate={composeValidators(required, minLength(2), maxLength(50), isValidName)}
                        >
                          {({ input, meta }) => (
                            <div className="field">
                              <label
                                htmlFor="firstName"
                                className="block text-sm sm:text-base font-semibold mb-2 text-gray-900"
                              >
                                First Name <span className="text-red-500">*</span>
                              </label>
                              <InputText
                                {...input}
                                id="firstName"
                                data-testid="signup-txt-firstname"
                                className={`w-full text-base sm:text-lg p-3 sm:p-4 ${meta.error && meta.touched ? "p-invalid" : ""}`}
                                placeholder="Enter your first name"
                                onChange={(e) => {
                                  const cleaned = e.target.value.replace(/[^A-Za-z\s'-]/g, "").slice(0, 50);
                                  input.onChange(cleaned);
                                }}
                              />
                              {meta.error && meta.touched && (
                                <small className="p-error block mt-2 text-sm">{meta.error}</small>
                              )}
                            </div>
                          )}
                        </Field>

                        <Field
                          name="lastName"
                          validate={composeValidators(required, minLength(2), maxLength(50), isValidName)}
                        >
                          {({ input, meta }) => (
                            <div className="field">
                              <label
                                htmlFor="lastName"
                                className="block text-sm sm:text-base font-semibold mb-2 text-gray-900"
                              >
                                Last Name <span className="text-red-500">*</span>
                              </label>
                              <InputText
                                {...input}
                                id="lastName"
                                data-testid="signup-txt-lastname"
                                className={`w-full text-base sm:text-lg p-3 sm:p-4 ${meta.error && meta.touched ? "p-invalid" : ""}`}
                                placeholder="Enter your last name"
                                onChange={(e) => {
                                  const cleaned = e.target.value.replace(/[^A-Za-z\s'-]/g, "").slice(0, 50);
                                  input.onChange(cleaned);
                                }}
                              />
                              {meta.error && meta.touched && (
                                <small className="p-error block mt-2 text-sm">{meta.error}</small>
                              )}
                            </div>
                          )}
                        </Field>

                        <Field
                          name="mobile"
                          validate={composeValidators(required, minLength(10), maxLength(10), isMobile)}
                        >
                          {({ input, meta }) => (
                            <div className="field">
                              <label
                                htmlFor="mobile"
                                className="block text-sm sm:text-base font-semibold mb-2 text-gray-900"
                              >
                                Mobile <span className="text-red-500">*</span>
                              </label>
                              <InputText
                                {...input}
                                id="mobile"
                                data-testid="signup-txt-mobile"
                                maxLength={10}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                className={`w-full text-base sm:text-lg p-3 sm:p-4 ${meta.error && meta.touched ? "p-invalid" : ""}`}
                                placeholder="0400 000 000"
                                onChange={(e) => {
                                  const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 10);
                                  input.onChange(digitsOnly);
                                }}
                              />
                              {meta.error && meta.touched && (
                                <small className="p-error block mt-2 text-sm">{meta.error}</small>
                              )}
                            </div>
                          )}
                        </Field>

                        <Field name="email" validate={composeValidators(required, isEmail)}>
                          {({ input, meta }) => (
                            <div className="field">
                              <label
                                htmlFor="email"
                                className="block text-sm sm:text-base font-semibold mb-2 text-gray-900"
                              >
                                Email <span className="text-red-500">*</span>
                              </label>
                              <InputText
                                {...input}
                                id="email"
                                data-testid="signup-txt-email"
                                type="email"
                                className={`w-full text-base sm:text-lg p-3 sm:p-4 ${meta.error && meta.touched ? "p-invalid" : ""}`}
                                placeholder="your.email@example.com"
                              />
                              {meta.error && meta.touched && (
                                <small className="p-error block mt-2 text-sm">{meta.error}</small>
                              )}
                            </div>
                          )}
                        </Field>

                        <Field name="confirmEmail" validate={composeValidators(required, isEmail)}>
                          {({ input, meta }) => (
                            <div className="field">
                              <label
                                htmlFor="confirmEmail"
                                className="block text-sm sm:text-base font-semibold mb-2 text-gray-900"
                              >
                                Confirm Email <span className="text-red-500">*</span>
                              </label>
                              <InputText
                                {...input}
                                id="confirmEmail"
                                data-testid="signup-txt-confirmemail"
                                type="email"
                                className={`w-full text-base sm:text-lg p-3 sm:p-4 ${meta.error && meta.touched ? "p-invalid" : ""}`}
                                placeholder="Confirm your email"
                              />
                              {meta.error && meta.touched && (
                                <small className="p-error block mt-2 text-sm">{meta.error}</small>
                              )}
                            </div>
                          )}
                        </Field>

                        <Field name="acceptTerms" type="checkbox" validate={mustBeChecked}>
                          {({ input, meta }) => (
                            <div className="field-checkbox bg-gray-50 p-4 rounded-lg border border-gray-200">
                              <div className="flex items-start gap-3">
                                <Checkbox
                                  inputId="acceptTerms"
                                  data-testid="signup-chk-acceptterms"
                                  checked={input.checked || false}
                                  onChange={(e) => input.onChange(e.checked)}
                                  className={`mt-1 ${meta.error && meta.touched ? "p-invalid" : ""}`}
                                />
                                <label
                                  htmlFor="acceptTerms"
                                  className="text-sm sm:text-base leading-relaxed text-gray-700 cursor-pointer"
                                >
                                  I accept the{" "}
                                  <a
                                    href="https://info.realestateinvestar.com.au/terms-conditions-v6"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    id="signup-lnk-terms"
                                    data-testid="signup-lnk-terms"
                                    className="text-blue-600 hover:text-blue-700 underline font-medium"
                                  >
                                    Terms and Conditions
                                  </a>
                                  <span className="text-red-500 ml-1">*</span>
                                </label>
                              </div>
                              {meta.error && meta.touched && (
                                <small className="p-error block mt-2 ml-8 text-sm">{meta.error}</small>
                              )}
                            </div>
                          )}
                        </Field>

                        <Message
                          severity="info"
                          text="By completing this form, you will receive occasional property investing emails from Real Estate Investar. You can unsubscribe at any time."
                          className="w-full text-sm sm:text-base"
                        />

                        <Button
                          type="submit"
                          id="signup-btn-continue"
                          data-testid="signup-btn-continue"
                          label={isSubmitting ? "Processing..." : "Continue to Payment"}
                          icon="pi pi-arrow-right"
                          iconPos="right"
                          className="w-full p-button-lg font-semibold text-base sm:text-lg py-4 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02]"
                          disabled={isSubmitting}
                          loading={isSubmitting}
                          onClick={() => {}}
                        />

                        <p className="text-xs sm:text-sm text-gray-500 text-center pt-2">
                          <span className="text-red-500">*</span> = Required Fields
                        </p>
                      </form>
                    )}
                  />
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ContactDetailsStep;
